import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import SearchPalette from "@/features/search/SearchPalette";
import { togglePalette } from "@/features/search/paletteStore";
import Loader from "@/shared/components/Loader";
import { MODULES } from "@/shared/config/modules";
import { LEGACY_IFRAME_URL } from "@/shared/config/legacyRoutes";
import AuthHeader from "@/shared/layout/components/AuthHeader";
import { useCapabilities } from "@/shared/hooks/useCapabilities";
import { useHotkey } from "@/shared/hooks/useHotkey";
import SideBar from "@/shared/layout/components/Sidebar";
import { getRouteTitle, isLegacyIframePath } from "@/shared/lib/routeMeta";
import FloatingButtons from "../components/FloatingButtons";

export default function AuthLayout() {
  const pathname = useLocation().pathname;
  const { isModuleEnabled } = useCapabilities();
  const searchOn = isModuleEnabled(MODULES.SEARCH);
  useHotkey("mod+k", togglePalette, searchOn);

  // Local pages render the outlet; screens still on the legacy prototype render its iframe.
  const isLegacyScreen = isLegacyIframePath(pathname);

  const iframeSrc = useMemo(() => `${LEGACY_IFRAME_URL}${pathname}?embed=true`, [pathname]);
  const loadingMessage = useMemo(() => `Loading ${getRouteTitle(pathname)}...`, [pathname]);

  const [isIframeLoading, setIsIframeLoading] = useState(isLegacyScreen);

  useEffect(() => {
    setIsIframeLoading(isLegacyScreen);
  }, [isLegacyScreen, pathname]);

  return (
    <div className="h-screen flex overflow-hidden">
      <SideBar />

      <main className="flex-1 flex flex-col overflow-hidden">
        <AuthHeader />

        <div className="flex-1 overflow-auto thin-scrollbar m-2">
          {!isLegacyScreen ? (
            <Outlet />
          ) : (
            <div className="relative h-full w-full">
              {isIframeLoading ? (
                <Loader
                  message={loadingMessage}
                  className="absolute inset-0 z-10 bg-theme-surface"
                />
              ) : null}

              <iframe
                src={iframeSrc}
                title="App"
                onLoad={() => setIsIframeLoading(false)}
                className={`h-full w-full border-none transition-opacity duration-200 ${
                  isIframeLoading ? "opacity-0" : "opacity-100"
                }`}
              />
            </div>
          )}
        </div>
      </main>
      <FloatingButtons />
      {searchOn ? <SearchPalette /> : null}
    </div>
  );
}
